import { useCallback } from 'react';
import { DEFAULT_PREFERENCES, type DefaultPreferencesType } from '../DEFAULTS';
import { type PreferenceDict, type PreferenceKey } from '../types';
import { useUserPreferencesStore, userPreferencesActions, updatePreferenceInMain } from './user-preferences-store';

type UserPreferenceStoreState = {
  prefs: Record<string, any>;
  isHydrated: boolean;
};

function usePreferenceStoreSubscription<K extends PreferenceKey, T>(
  id: K,
  selector: ((value: any) => T) | undefined,
  actualOmitDefaultValue: boolean
) {
  const getRawValue = useCallback(
    (prefData: any) => {
      return (
        prefData?.value ?? (!actualOmitDefaultValue ? DEFAULT_PREFERENCES[id as DefaultPreferencesType] : undefined)
      );
    },
    [id, actualOmitDefaultValue]
  );

  const storeSelector = useCallback(
    (state: UserPreferenceStoreState) => {
      const prefData = state.prefs[id];
      const rawValue = getRawValue(prefData);

      return {
        value: selector ? selector(rawValue) : rawValue,
        isDataLoaded: !!prefData,
        updatedAt: prefData?.updatedAt,
        isHydrated: state.isHydrated,
      };
    },
    [id, getRawValue, selector]
  );

  const equalityFn = useCallback(
    (prev: any, next: any) => {
      if (selector) {
        return (
          prev.value === next.value &&
          prev.isDataLoaded === next.isDataLoaded &&
          prev.updatedAt === next.updatedAt &&
          prev.isHydrated === next.isHydrated
        );
      }

      return (
        prev.isDataLoaded === next.isDataLoaded &&
        prev.updatedAt === next.updatedAt &&
        prev.isHydrated === next.isHydrated
      );
    },
    [selector]
  );

  return useUserPreferencesStore(storeSelector, equalityFn);
}

function usePreferenceSetter<K extends PreferenceKey>(id: K) {
  return useCallback(
    (newValue: PreferenceDict[K]) => {
      updatePreferenceInMain(id, newValue);
    },
    [id]
  );
}

/**
 * Hook for managing user preferences in the notification app.
 * This is similar to the main app's usePreference hook but adapted for IPC communication.
 */
type UseNotificationsPreferenceOptions<K extends PreferenceKey, T = never, D extends boolean = false> = {
  selector?: (
    value: K extends DefaultPreferencesType
      ? D extends true
        ? PreferenceDict[K] | undefined
        : PreferenceDict[K]
      : PreferenceDict[K] | undefined
  ) => T;
  omitDefaultValue?: D;
  enabled?: boolean;
};

export function useNotificationPreference<K extends PreferenceKey, T = never, D extends boolean = false>(
  id: K,
  options?: UseNotificationsPreferenceOptions<K, T, D>
): [
  [T] extends [never]
    ? K extends DefaultPreferencesType
      ? D extends true
        ? PreferenceDict[K] | undefined
        : PreferenceDict[K]
      : PreferenceDict[K] | undefined
    : T,
  (value: PreferenceDict[K]) => void,
  { isLoading: boolean }
] {
  options = options || {};
  const { selector, omitDefaultValue = false } = options;

  const { value, isHydrated } = usePreferenceStoreSubscription(id, selector, omitDefaultValue);

  const setPreference = usePreferenceSetter(id);

  const isLoading = !isHydrated;

  return [value, setPreference, { isLoading }];
}

export function requestUserPreferencesSync(): void {
  if (window.shell) {
    window?.shell?.invoke?.('user-preferences:sync', undefined)?.catch((error) => {
      console.error('Failed to request user preferences sync:', error);
    });
  }
}

export { userPreferencesActions };
